import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from './AuthContext';
import {
  Container,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Modal,
  Fade,
  IconButton,
  Stack,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

import { tokens, ColorModeContext } from './theme';
import LoginPage from './pages/login/Login';
import LogoBlog from './assets/logo.png';

const didYouKnowFacts = [
  'Effective time management can increase productivity by up to 20%.',
  'The Pomodoro Technique uses 25-minute work sessions to boost focus.',
  'Regular breaks can improve mental agility and reduce fatigue.',
  'Task prioritization helps reduce stress and meet deadlines.',
  'The Eisenhower Matrix categorizes tasks by urgency and importance.',
  'Multitasking can reduce productivity by up to 40%.',
  'Setting SMART goals improves project success rates.',
  'Collaboration tools can cut email usage by 30%.',
  'A tidy workspace can enhance focus and efficiency.',
  'Daily planning can save hours of wasted time weekly.',
  'The 80/20 rule suggests 20% of tasks produce 80% of results.',
  'Time tracking helps identify productivity bottlenecks.',
  'Delegation improves team efficiency and morale.',
  'Mind mapping aids in brainstorming and idea organization.',
  'The Kanban method visualizes workflow for better task management.',
  'Regular feedback loops improve team performance.',
  'Setting deadlines increases task completion rates.',
  'Morning routines can set a positive tone for the workday.',
  'The Zeigarnik Effect makes unfinished tasks more memorable.',
  'Batch processing similar tasks saves time and mental energy.',
  'Effective meetings can reduce project delays by 15%.',
  'The Pareto Principle helps focus on high-impact tasks.',
  'Automating repetitive tasks can save hours daily.',
  'Clear communication reduces workplace errors.',
  'Goal setting aligns teams with organizational objectives.',
  'The 2-Minute Rule encourages starting small tasks immediately.',
  'Time-blocking enhances focus on high-priority tasks.',
  'Regular training improves employee skillsets.',
  'A positive work environment boosts productivity.',
  'Task delegation empowers team members.',
  'The Getting Things Done (GTD) method organizes tasks effectively.',
  'Mindfulness practices reduce workplace stress.',
  'Prioritizing sleep improves cognitive performance.',
  'The 4D method: Do, Defer, Delegate, or Delete tasks.',
  'Project management software streamlines workflows.',
  'The Feynman Technique enhances learning through teaching.',
  'Regular reviews help track project progress.',
  'The Rule of Three focuses on three key daily tasks.',
  'Active listening improves team collaboration.',
  'Setting boundaries prevents work overload.',
  'The Ivy Lee Method prioritizes six tasks daily.',
  'Time audits reveal how time is actually spent.',
  'The Agile methodology enhances team adaptability.',
  'Regular exercise boosts workplace energy levels.',
  'The 1-3-5 Rule: Plan 1 big, 3 medium, and 5 small tasks daily.',
  'Effective onboarding reduces employee turnover.',
  'The Scrum framework improves team productivity.',
  'Taking notes enhances task retention.',
  'The OKR framework aligns goals with measurable results.',
  'Continuous learning fosters innovation.',
];

const FACT_INTERVAL = 10000; // ms
const FACT_FADE = 400; // ms

const HomePage = () => {
  const { isAuthenticated, userId, userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [openLoginModal, setOpenLoginModal] = useState(false);
  const [currentFactIndex, setCurrentFactIndex] = useState(() =>
    Math.floor(Math.random() * didYouKnowFacts.length)
  );
  const [factIn, setFactIn] = useState(true);

  /* ------------------------------------------------------------------ */
  /* Derived design tokens                                               */
  /* ------------------------------------------------------------------ */
  const ui = useMemo(() => {
    const accent = colors.teal[500];      // #0aa125
    const accent2 = colors.newTeal[500];  // #0dcaf0
    const success = colors.green[500];    // #198754

    return {
      accent,
      accent2,
      success,
      pageBg: isDark ? colors.dark[800] : '#e9ebef',
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
      glow: isDark ? alpha(accent, 0.22) : alpha(accent, 0.14),
      shadow: isDark
        ? '0 18px 40px -18px rgba(0,0,0,0.75)'
        : '0 18px 40px -22px rgba(16,24,74,0.35)',
    };
  }, [colors, isDark]);

  /* ------------------------------------------------------------------ */
  /* Redirect authenticated users                                        */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (isAuthenticated) {
      const intendedPath =
        location.state?.from?.pathname ||
        (userRole === 'user' ? '/user/dashboard' : '/admin/dashboard');
      navigate(intendedPath, { replace: true });
    }
  }, [isAuthenticated, userRole, location, navigate]);

  /* ------------------------------------------------------------------ */
  /* Rotate "Did You Know" facts with a crossfade                        */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    let swapTimer;
    const interval = setInterval(() => {
      setFactIn(false);
      swapTimer = setTimeout(() => {
        setCurrentFactIndex((prev) => (prev + 1) % didYouKnowFacts.length);
        setFactIn(true);
      }, FACT_FADE);
    }, FACT_INTERVAL);

    return () => {
      clearInterval(interval);
      clearTimeout(swapTimer);
    };
  }, []);

  const handleOpenLogin = () => setOpenLoginModal(true);
  const handleCloseLogin = () => setOpenLoginModal(false);

  /* ------------------------------------------------------------------ */
  /* Reusable styles                                                     */
  /* ------------------------------------------------------------------ */
  const primaryCta = {
    px: 3.5,
    py: 1.35,
    borderRadius: 2,
    fontWeight: 700,
    fontSize: 14,
    letterSpacing: 0.4,
    textTransform: 'none',
    color: '#ffffff',
    background: ui.gradient,
    boxShadow: `0 10px 24px -10px ${alpha(ui.accent, 0.9)}`,
    transition: 'transform .2s ease, box-shadow .2s ease, filter .2s ease',
    '&:hover': {
      filter: 'brightness(1.06)',
      transform: 'translateY(-2px)',
      boxShadow: `0 16px 30px -12px ${alpha(ui.accent, 0.95)}`,
    },
  };

  const features = [
    {
      icon: <TaskAltOutlinedIcon />,
      title: 'Task Management',
      text: 'Plan, assign and track work from a single board.',
    },
    {
      icon: <GroupsOutlinedIcon />,
      title: 'Team Collaboration',
      text: 'Keep every department aligned and accountable.',
    },
    {
      icon: <InsightsOutlinedIcon />,
      title: 'Insights & Reports',
      text: 'Turn daily activity into measurable performance.',
    },
  ];

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: ui.pageBg,
        color: ui.textPrimary,
        position: 'relative',
        overflow: 'hidden',
        // ambient brand glow
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -220,
          right: -160,
          width: 620,
          height: 620,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${ui.glow} 0%, transparent 68%)`,
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -260,
          left: -180,
          width: 560,
          height: 560,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(ui.accent2, isDark ? 0.16 : 0.12)} 0%, transparent 68%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDark ? 'dark' : 'light'}
      />

      {/* ---------------------------------------------------------------- */}
      {/* Header                                                            */}
      {/* ---------------------------------------------------------------- */}
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          borderBottom: `1px solid ${ui.border}`,
          bgcolor: alpha(ui.surface, isDark ? 0.72 : 0.82),
          backdropFilter: 'blur(14px)',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              height: 68,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: { xs: 120, sm: 148 },
                display: 'flex',
                alignItems: 'center',
                px: isDark ? 1.25 : 0,
                py: isDark ? 0.75 : 0,
                borderRadius: 1.5,
                bgcolor: isDark ? alpha('#ffffff', 0.94) : 'transparent',
              }}
            >
              <img
                src={LogoBlog}
                alt="Mindsinaction"
                style={{ width: '100%', display: 'block' }}
              />
            </Box>

            <Stack direction="row" spacing={1.25} alignItems="center">
              <Tooltip title={isDark ? 'Light mode' : 'Dark mode'}>
                <IconButton
                  onClick={colorMode.toggleColoeMode}
                  sx={{
                    color: ui.textSecondary,
                    border: `1px solid ${ui.border}`,
                    borderRadius: 1.5,
                    '&:hover': {
                      color: ui.accent,
                      borderColor: alpha(ui.accent, 0.5),
                      bgcolor: alpha(ui.accent, 0.08),
                    },
                  }}
                >
                  {isDark ? (
                    <LightModeOutlinedIcon fontSize="small" />
                  ) : (
                    <DarkModeOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>

              <Button
                onClick={handleOpenLogin}
                startIcon={<LoginOutlinedIcon />}
                sx={{ ...primaryCta, px: 2.5, py: 1 }}
              >
                Open My Office
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* ---------------------------------------------------------------- */}
      {/* Hero                                                              */}
      {/* ---------------------------------------------------------------- */}
      <Container
        sx={{
          maxWidth: '1200px !important',
          height: '100vh !important',
          flex: 1,
          position: 'relative',
          zIndex: 1,
          py: { xs: 6, md: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.75,
            py: 0.6,
            mb: 3,
            borderRadius: 999,
            border: `1px solid ${alpha(ui.accent, 0.35)}`,
            bgcolor: alpha(ui.accent, isDark ? 0.12 : 0.09),
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: ui.accent,
              boxShadow: `0 0 0 4px ${alpha(ui.accent, 0.18)}`,
            }}
          />
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, letterSpacing: 0.6, color: ui.accent }}
          >
            Work Management System
          </Typography>
        </Box>

        <Typography
          variant={isMobile ? 'h2' : 'h1'}
          component="h1"
          sx={{
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: -0.5,
            maxWidth: 880,
            color: ui.textPrimary,
          }}
        >
          Welcome to{' '}
          <Box
            component="span"
            sx={{
              background: ui.gradient,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Mindsinaction
          </Box>
          {userId ? `, ${userId}` : ''}
        </Typography>

        <Typography
          variant="h5"
          sx={{
            mt: 2.5,
            maxWidth: 620,
            lineHeight: 1.7,
            color: ui.textSecondary,
          }}
        >
          Your work management system to streamline tasks, collaborate with your
          team, and achieve goals — all in one office.
        </Typography>

        {/* -------------------------------------------------------------- */}
        {/* Did You Know                                                    */}
        {/* -------------------------------------------------------------- */}
        <Card
          id="did-you-know"
          elevation={0}
          sx={{
            mt: { xs: 6, md: 8 },
            width: '100%',
            maxWidth: 680,
            textAlign: 'left',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            border: `1px solid ${ui.border}`,
            bgcolor: ui.surface,
            boxShadow: ui.shadow,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              width: 4,
              background: ui.gradient,
            },
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, pl: { xs: 3, md: 4 } }}>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
              <InfoOutlinedIcon sx={{ color: ui.accent, fontSize: 20 }} />
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  textTransform: 'uppercase',
                  color: ui.accent,
                }}
              >
                Did You Know?
              </Typography>
            </Stack>

            <Fade in={factIn} timeout={FACT_FADE}>
              <Typography
                variant="h4"
                sx={{
                  minHeight: 72,
                  fontWeight: 500,
                  lineHeight: 1.55,
                  color: ui.textPrimary,
                }}
              >
                {didYouKnowFacts[currentFactIndex]}
              </Typography>
            </Fade>

            <Divider sx={{ my: 2, borderColor: ui.border }} />

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                Tip {currentFactIndex + 1} of {didYouKnowFacts.length}
              </Typography>

              {/* progress bar, restarts on every fact */}
              <Box
                key={currentFactIndex}
                sx={{
                  width: 120,
                  height: 3,
                  borderRadius: 999,
                  overflow: 'hidden',
                  bgcolor: alpha(ui.textSecondary, 0.22),
                  '& > span': {
                    display: 'block',
                    height: '100%',
                    background: ui.gradient,
                    transformOrigin: 'left',
                    animation: `factProgress ${FACT_INTERVAL}ms linear forwards`,
                  },
                  '@keyframes factProgress': {
                    from: { transform: 'scaleX(0)' },
                    to: { transform: 'scaleX(1)' },
                  },
                }}
              >
                <span />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Container>

      {/* ---------------------------------------------------------------- */}
      {/* Login modal                                                       */}
      {/* ---------------------------------------------------------------- */}
      <Modal
        open={openLoginModal}
        onClose={handleCloseLogin}
        closeAfterTransition
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: alpha(isDark ? '#03050f' : '#10184a', 0.6),
              backdropFilter: 'blur(6px)',
            },
          },
        }}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
      >
        <Fade in={openLoginModal}>
          <Box
            sx={{
              outline: 'none',
              position: 'relative',
              borderRadius: 3,
              overflow: 'hidden',
              maxWidth: '100%',
              maxHeight: '92vh',
              overflowY: 'auto',
              border: `1px solid ${ui.border}`,
              bgcolor: ui.surface,
              boxShadow: ui.shadow,
            }}
          >
            <IconButton
              onClick={handleCloseLogin}
              aria-label="Close login"
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 5,
                color: ui.textSecondary,
                bgcolor: alpha(ui.surfaceAlt, 0.7),
                '&:hover': {
                  color: colors.red[500],
                  bgcolor: alpha(colors.red[500], 0.12),
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            <LoginPage />
          </Box>
        </Fade>
      </Modal>
      {/* ---------------------------------------------------------------- */}
      {/* Footer                                                            */}
      {/* ---------------------------------------------------------------- */}
      <Box
        component="footer"
        sx={{
          position: 'relative',
          zIndex: 1,
          borderTop: `1px solid ${ui.border}`,
          bgcolor: alpha(ui.surface, isDark ? 0.5 : 0.7),
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: 2.5 }}
          >
            <Typography variant="h6" sx={{ color: ui.textSecondary }}>
              © {new Date().getFullYear()} Mindsinaction. All rights reserved.
            </Typography>
            <Typography variant="h6" sx={{ color: ui.textSecondary }}>
              Built for teams that get things done.
            </Typography>
          </Stack>
        </Container>
      </Box>

      
    </Box>
  );
};

export default HomePage;